import React, { useEffect, useState } from "react";
import { Button, Input, Spinner, Table, UncontrolledTooltip } from "reactstrap";
import instance from "../../../axiosInstance";
import { useUserContext } from "../../../context/security/users";
import { toDateAndTimeObj } from "../../../utils/forms";
import Pagination from "../../UI/Pagination";
import TableFeedback from "../../UI/TableFeedback";
import ParamsLink from "../../UI/links/ParamsLink";
import useError from "../../../hooks/useError";

const UserNotes = () => {
  const { userId } = useUserContext();
  const { handleError } = useError();
  const [notes, setNotes] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [leadSourceId, setLeadSourceId] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 0,
    totalPages: 0,
    totalRecords: 0,
    limit: 0,
  });

  // debounce typing so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    const getNotes = async () => {
      try {
        setIsLoading(true);
        const response = await instance.get(`/user/sourceCaseNotes/${userId}`, { params: { page, limit, search } });
        const data = response.data.data;
        setNotes(data.data);
        setLeadSourceId(data.lead_source_id);
        setPaginationInfo({
          currentPage: data.page,
          totalPages: data.total_pages,
          totalRecords: data.total_records,
          limit: data.limit,
        });
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };
    if (userId) getNotes();
  }, [userId, page, limit, search]);

  const handlePageChange = (selectedPage) => setPage(selectedPage.selected + 1);

  return (
    <div className="p-2">
      <div className="d-flex justify-content-between align-items-center p-2">
        <h4 className="m-0">Notes</h4>
        {leadSourceId ? (
          <ParamsLink to={`/workProvider/sourceCase/${leadSourceId}/edit`}>
            <Button color="primary">Add</Button>
          </ParamsLink>
        ) : (
          <span id="add-note-disabled" className="d-inline-block">
            <Button color="primary" disabled style={{ pointerEvents: "none" }}>
              Add
            </Button>
            <UncontrolledTooltip target="add-note-disabled">No source case found for this user's email</UncontrolledTooltip>
          </span>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-center px-2 pb-2">
        <div className="d-flex align-items-center">
          <label htmlFor="user-notes-limit" className="m-0 me-2 mr-2">Rows per page</label>
          <Input type="select" id="user-notes-limit" bsSize="sm" style={{ width: "5.5rem" }} value={limit} onChange={handleLimitChange}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Input>
        </div>
        <Input
          type="search"
          placeholder="Search notes..."
          bsSize="sm"
          style={{ width: "16rem" }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>
      <Table className="table" responsive striped>
        <thead>
          <tr>
            <th scope="col">Sr.#</th>
            <th scope="col">Date & Time</th>
            <th scope="col">Case</th>
            <th scope="col" style={{ maxWidth: "500px" }}>Note</th>
            <th scope="col">Note By</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan="5" className="text-center">
                <Spinner color="primary" style={{ width: "2rem", height: "2rem" }} />
              </td>
            </tr>
          )}
          {!isLoading && notes.length === 0 && <TableFeedback message="No notes found" colSpan="5" />}
          {!isLoading &&
            notes.map((note, index) => (
              <tr key={index}>
                <th scope="row">{(paginationInfo.currentPage - 1) * paginationInfo.limit + index + 1}</th>
                <td>{toDateAndTimeObj(note.created_at)}</td>
                <td>{note.case_name}</td>
                <td dangerouslySetInnerHTML={{ __html: note.description }} />
                <td>{note.creater_name}</td>
              </tr>
            ))}
        </tbody>
      </Table>
      <Pagination paginationInfo={paginationInfo} handlePageChange={handlePageChange} />
    </div>
  );
};

export default UserNotes;
